import Head from 'next/head';
import { useTranslation } from 'next-i18next';

import { useMemo } from 'react';

import { RegexConst } from '@nocowanie/core/consts';
import { ImageRatio, ImageSize } from '@nocowanie/core/enums';
import { formatHelpers, imageHelpers, urlHelpers } from '@nocowanie/core/helpers';

import { META_IMAGE_DIMENSIONS } from '@app/consts';
import { environment } from '@app/environments/environment';
import { apiTypes } from '@app/models';
import * as accommodationStaticSlice from '@app/state/slices/accommodation-static.slice';
import { useAppSelector } from '@app/state/state';

import { RichSnippetsProps } from './rich-snippets.props';

export const RichSnippets = ({ accommodationId }: RichSnippetsProps) => {
    const { t } = useTranslation('accommodation', {
        keyPrefix: 'richSnippets',
    });

    const {
        type,
        name,
        description,
        numberOfRooms,
        image,
        addressCountry,
        addressLocality,
        addressRegion,
        postalCode,
        streetAddress,
        latitude,
        longitude,
        ratingAverage,
        ratingCount,
        services,
        internalAmenityGroups,
        labels,
        cheapestRate,
        currency,
        convertedCurrency,
    } = useAppSelector(accommodationStaticSlice.selectors.selectRichSnippetsData);

    const url = useMemo(() => {
        return name && accommodationId
            ? urlHelpers.getAccommodationUrl(accommodationId, name, {}, environment.NOCOWANIE_URL)
            : undefined;
    }, [accommodationId, name]);

    const displayedType = useMemo(() => {
        switch (type) {
            case apiTypes.HotelTypeEnum['Hotel']:
                return apiTypes.HotelTypeEnum['Hotel'];
            case apiTypes.HotelTypeEnum['Hostel']:
                return apiTypes.HotelTypeEnum['Hostel'];
            case apiTypes.HotelTypeEnum['Motel']:
                return apiTypes.HotelTypeEnum['Motel'];
            default:
                return 'LodgingBusiness';
        }
    }, [type]);

    const imageVariants = image
        ? [
              imageHelpers.wpresizer(META_IMAGE_DIMENSIONS, image),
              imageHelpers.wpresizer({ ratio: ImageRatio['4/3'], size: ImageSize.Xl }, image),
              imageHelpers.wpresizer({ ratio: ImageRatio['16/9'], size: ImageSize.Xl }, image),
          ]
        : undefined;

    const amenityNames = services?.map(group => group.name);

    const trimmedDesc = useMemo(() => {
        if (!description) return undefined;

        // Strip HTML tags and markdown to plain text for JSON-LD.
        // CMS returns markdown (AI descriptions) or HTML (regular descriptions).
        const desc = description
            .replace(RegexConst.HTML_TAGS, ' ')
            .replace(RegexConst.MARKDOWN_BOLD_ITALIC, '$1')
            .replace(/\s+/g, ' ')
            .trim();

        return desc ? (desc.length > 300 ? `${desc.substring(0, 300)}...` : desc) : undefined;
    }, [description]);

    const price = useMemo(() => {
        const rate = cheapestRate?.convertedRate ? cheapestRate?.convertedRate : cheapestRate?.rate;

        // if we have more than one guest, we divide the price by their (guests) number
        return rate && cheapestRate?.guests && cheapestRate.guests > 1
            ? Math.round(rate / cheapestRate.guests)
            : rate;
    }, [cheapestRate]);

    // convertedCurrency isn't in ElasticSearch, so we set the default value
    const convertedCurrencyCode = convertedCurrency ? convertedCurrency : 'PLN';

    // we need to remap 'PLN' to 'zł'
    const formattedCurrency = formatHelpers.price(0, convertedCurrencyCode);
    const displayedConvertedCurrency = formattedCurrency.trim().split(/\s+/).pop();

    const displayedCurrency = cheapestRate?.convertedRate ? displayedConvertedCurrency : currency;

    const petsAllowed = useMemo(() => {
        const petsInLabels = labels?.filter(label => label == apiTypes.Label['PetFriendly']);
        const petsInAmenities = internalAmenityGroups?.filter(
            group => group.type == apiTypes.InternalAmenityGroupEnum['PetFriendly'],
        );
        const petsInServices = services?.filter(
            service =>
                service.type == apiTypes.ServiceType['PetFriendly'] ||
                service.type == apiTypes.ServiceType['PetsAreAllowed'],
        );

        return !!petsInLabels?.length || !!petsInAmenities?.length || !!petsInServices?.length;
    }, [services, internalAmenityGroups, labels]);

    if (!type || !name) return;

    const schemaObj = {
        '@context': 'https://schema.org/',
        '@type': displayedType,
        name,
        ...(trimmedDesc ? { description: trimmedDesc } : null),
        ...(numberOfRooms ? { numberOfRooms: numberOfRooms } : null),
        ...(url ? { tourBookingPage: url } : null),
        petsAllowed,
        ...(imageVariants ? { image: imageVariants } : null),
        ...(addressCountry || addressLocality || addressRegion || postalCode || streetAddress
            ? {
                  address: {
                      '@type': 'PostalAddress',
                      ...(addressCountry ? { addressCountry: addressCountry } : null),
                      ...(addressLocality ? { addressLocality: addressLocality } : null),
                      ...(addressRegion ? { addressRegion: addressRegion } : null),
                      ...(postalCode ? { postalCode: postalCode } : null),
                      ...(streetAddress ? { streetAddress: streetAddress } : null),
                  },
              }
            : null),
        ...(latitude && longitude
            ? {
                  geo: {
                      '@type': 'GeoCoordinates',
                      latitude: latitude,
                      longitude: longitude,
                  },
              }
            : null),
        ...(ratingAverage && ratingCount
            ? {
                  aggregateRating: {
                      '@type': 'AggregateRating',
                      ratingValue: ratingAverage,
                      ratingCount: ratingCount,
                      bestRating: 10,
                      worstRating: 1,
                  },
              }
            : null),
        ...(amenityNames
            ? {
                  amenityFeature: {
                      '@type': 'LocationFeatureSpecification',
                      value: 'True',
                      name: amenityNames,
                  },
              }
            : null),
        ...(latitude && longitude
            ? { hasMap: `https://www.openstreetmap.org/#map=15/${latitude}/${longitude}` }
            : null),
        ...{
            priceRange:
                price && currency
                    ? t('cheapestPrice', { value: price, value2: displayedCurrency })
                    : t('noPriceAvailable'),
        },
    };

    return (
        <Head>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaObj) }}
            />
        </Head>
    );
};
